import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { directivesApi } from "../services/api";
import { format } from "date-fns";

const DirectiveCard = ({ compact = false }) => {
  const [directiveInfo, setDirectiveInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    fetchDirectiveInfo();
  }, []);

  const fetchDirectiveInfo = async () => {
    try {
      setLoading(true);
      const response = await directivesApi.info();
      setDirectiveInfo(response.data.directive);
    } catch (err) {
      // 404 is okay - means no directive yet
      if (err.response?.status !== 404) {
        setError(t("directive.failedToLoad"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDirective = async () => {
    try {
      const response = await directivesApi.view();
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      setError(t("directive.failedToView"));
    }
  };

  const handleDownloadDirective = async () => {
    try {
      const response = await directivesApi.download();
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = directiveInfo?.fileName || "directive.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t("directive.failedToDownload"));
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={30} />
        </CardContent>
      </Card>
    );
  }

  if (!directiveInfo) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PdfIcon color="action" sx={{ fontSize: 40, mr: 2 }} />
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("directive.notFound")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("directive.uploadAdmin")}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={() => navigate("/directives")}
          >
            {t("directive.goToDirectives")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <PdfIcon color="error" sx={{ mr: 1 }} />
            <Typography variant="subtitle1" noWrap>
              {t("directive.current")}
            </Typography>
            <Chip
              label={`${t("directive.versionPrefix")}${directiveInfo.version}`}
              size="small"
              color="primary"
              sx={{ ml: "auto" }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" noWrap>
            {directiveInfo.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Year: {directiveInfo.year} | {directiveInfo.fileSize}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: "space-between", pt: 0 }}>
          <Button size="small" startIcon={<ViewIcon />} onClick={handleViewDirective}>
            {t("common.view")}
          </Button>
          <Button size="small" startIcon={<InfoIcon />} onClick={() => navigate("/directives")}>
            {t("directive.details")}
          </Button>
        </CardActions>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
        }
      }}
    >
      <Box sx={{
        p: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Box sx={{
          p: 1.5,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.error.main, 0.1),
          display: 'flex'
        }}>
          <PdfIcon color="error" sx={{ fontSize: 32 }} />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight="800" color="text.primary">
            {t("directive.title")}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '1px' }}>
            {t("directive.subtitle")}
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{ p: 4 }}>
        <Typography
          variant="h5"
          fontWeight="700"
          color="primary.main"
          gutterBottom
          sx={{
            lineHeight: 1.3,
            mb: 3
          }}
        >
          {directiveInfo.title}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
          <Chip
            label={`${t("directive.issueYear")}: ${directiveInfo.year}`}
            color="primary"
            variant="filled"
            size="medium"
            sx={{ fontWeight: 'bold', borderRadius: 2 }}
          />
          <Chip
            label={`${t("directive.version")} ${directiveInfo.version}`}
            color="secondary"
            variant="filled"
            size="medium"
            sx={{ fontWeight: 'bold', borderRadius: 2 }}
          />
          <Chip
            label={directiveInfo.fileSize}
            icon={<InfoIcon />}
            variant="outlined"
            size="medium"
            sx={{ fontWeight: '600', borderRadius: 2 }}
          />
        </Box>

        <Alert
          severity="info"
          icon={<ViewIcon />}
          sx={{
            borderRadius: 3,
            bgcolor: alpha(theme.palette.info.main, 0.05),
            '& .MuiAlert-message': { fontWeight: 500 }
          }}
        >
          {t("directive.latestActiveMessage")}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
      </CardContent>

      <CardActions sx={{ p: 4, pt: 0, gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<ViewIcon />}
          onClick={handleViewDirective}
          sx={{
            flex: 2,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
          }}
        >
          {t("directive.viewPdf")}
        </Button>
        <Button
          variant="outlined"
          size="large"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadDirective}
          sx={{
            flex: 1,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 'bold'
          }}
        >
          {t("directive.getCopy")}
        </Button>
      </CardActions>
    </Card>
  );
};

export default DirectiveCard;